import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, Layers } from "lucide-react";
import api, { errMsg, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { REC_SCHEMAS } from "../config/recordSchemas";

const REC_TYPES = ["invoice", "dispatch", "transporter", "installer", "item"];

export default function RecordsReport() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const can = (p) => isAdmin || (user.permissions || []).includes(p);
  const available = REC_TYPES.filter((t) => can(`view_${t}`));
  const [recType, setRecType] = useState(available[0] || null);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ dateFrom: "", dateTo: "", customer: "", status: "" });

  const load = useCallback(async () => {
    if (!recType) return;
    try { const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v)); const { data } = await api.get(`/oa-records/${recType}`, { params }); setRows(data); }
    catch { setRows([]); }
  }, [recType, f]);
  useEffect(() => { load(); }, [load]);

  if (available.length === 0) return null;
  const schema = REC_SCHEMAS[recType] || {};

  const exportRec = (fmt) => {
    const params = { format: fmt, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
    api.get(`/oa-records/${recType}/export/all`, { params, responseType: "blob" })
      .then(({ data, headers }) => {
        const cd = headers["content-disposition"] || ""; const m = cd.match(/filename="?([^"]+)"?/);
        const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = m ? m[1] : `${recType}.${fmt}`; a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 60000);
        toast.success(`Exported ${recType} (${fmt})`);
      }).catch((e) => toast.error(errMsg(e)));
  };

  const total = recType === "invoice" ? rows.reduce((s, r) => s + (r.data?.totalAmount || 0), 0) : null;

  return (
    <div className="card overflow-hidden" data-testid="records-report">
      <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div className="font-head font-semibold flex items-center gap-2"><Layers className="h-4 w-4 text-amber-600" /> Module Records Report</div>
        <div className="flex items-center gap-2">
          <button className="btn-outline btn-sm" onClick={() => exportRec("excel")} data-testid="records-report-excel"><Download className="h-4 w-4" /> Excel</button>
          <button className="btn-outline btn-sm" onClick={() => exportRec("csv")} data-testid="records-report-csv"><Download className="h-4 w-4" /> CSV</button>
          <button className="btn-outline btn-sm" onClick={() => exportRec("pdf")} data-testid="records-report-pdf"><Download className="h-4 w-4" /> PDF</button>
        </div>
      </div>
      <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-slate-100">
        {available.map((t) => (
          <button key={t} onClick={() => setRecType(t)} data-testid={`records-report-type-${t}`}
            className={`btn-sm rounded-lg px-3 font-semibold ${recType === t ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700"}`}>
            {REC_SCHEMAS[t]?.title || t}
          </button>
        ))}
      </div>
      <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-slate-100">
        <input type="date" className="input" value={f.dateFrom} onChange={(e) => setF((p) => ({ ...p, dateFrom: e.target.value }))} data-testid="records-report-date-from" />
        <input type="date" className="input" value={f.dateTo} onChange={(e) => setF((p) => ({ ...p, dateTo: e.target.value }))} data-testid="records-report-date-to" />
        <input className="input" placeholder="Customer" value={f.customer} onChange={(e) => setF((p) => ({ ...p, customer: e.target.value }))} data-testid="records-report-customer" />
        {schema.hasStatus && <select className="input" value={f.status} onChange={(e) => setF((p) => ({ ...p, status: e.target.value }))} data-testid="records-report-status"><option value="">All Status</option>{(schema.statusOptions || []).map((s) => <option key={s} value={s}>{s}</option>)}</select>}
      </div>
      <div className="px-5 py-2 text-sm text-slate-500 flex gap-4">
        <span>Records: <b className="text-slate-900" data-testid="records-report-count">{rows.length}</b></span>
        {total != null && <span>Total Invoice Value: <b className="text-slate-900">{inr(total)}</b></span>}
      </div>
      <div className="overflow-x-auto max-h-96">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-y border-slate-200 sticky top-0"><tr>
            {(schema.columns || ["OA No.", "Customer"]).map((c) => <th key={c} className="th">{c}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={(schema.columns || []).length || 2}>No records.</td></tr>}
            {rows.slice(0, 200).map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                {(schema.columns || ["OA No.", "Customer"]).map((c) => (
                  <td key={c} className="td">{
                    c === "OA No." ? r.oaNumber :
                    c === "Customer" ? r.customerName :
                    c === "Total" ? inr(r.data?.totalAmount || 0) :
                    c === "Status" ? (r.status || "—") :
                    c === "Invoice No." ? (r.data?.invoiceNo || "—") :
                    c === "Total Weight" ? `${r.data?.totalWeight || 0} kg` :
                    c === "Team" ? (r.data?.teamName || "—") :
                    c === "Transport Name" ? (r.data?.transportName || "—") :
                    (r.data?.[Object.keys(r.data || {})[0]] ?? "—")
                  }</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
